import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import api from "@/lib/api";

const SEEN_KEY = "recoveros_welcome_v1";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const OPEN_AFTER_MS = 15000;      // 15s dwell
const SCROLL_THRESHOLD = 0.35;    // 35% page scroll
const AUTO_MINIMIZE_MS = 9000;    // auto-demote to chip if untouched
const track = (name, props) => { api.post("/events", { name, props }).catch(() => {}); };

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function useCountUp(target, run, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, run, duration]);
  return val;
}

const FEATURES = [
  "Money Stuck Analyzer™", "Recovery Health Score", "Daily Recovery Worksheet",
  "Weekly Executive Digest", "Top Accounts To Focus On", "Settlement Workflow",
];

const TRUST = [
  ["🔒", "SSL Secured"], ["💳", "Secure Payments by Razorpay"], ["📄", "GST Invoice"],
  ["🛡", "Multi-Tenant Security"], ["🔐", "Your Data Always Belongs To You"],
];

export function WelcomeModal({ onStartTrial }) {
  // view: "hidden" -> "modal" -> "chip" (reopenable) | "dismissed"
  const [view, setView] = useState("hidden");
  const viewedRef = useRef(false);   // ensures welcome_modal_view fires once per open cycle
  const autoMinRef = useRef(null);
  const closeBtnRef = useRef(null);

  const startAutoMinimize = useCallback(() => {
    clearTimeout(autoMinRef.current);
    autoMinRef.current = setTimeout(() => {
      setView("chip");
      track("welcome_modal_minimized");
    }, AUTO_MINIMIZE_MS);
  }, []);

  const openModal = useCallback(() => {
    setView("modal");
    if (!viewedRef.current) {
      viewedRef.current = true;
      track("welcome_modal_view", { trigger: "engaged_15s_35pct" });
    }
    // Set the 7-day cap the moment it opens — prevents refresh / back-button re-nag.
    localStorage.setItem(SEEN_KEY, String(Date.now()));
    startAutoMinimize();
  }, [startAutoMinimize]);

  // Trigger: open only when BOTH 15s dwell AND 35% scroll are satisfied.
  useEffect(() => {
    if (localStorage.getItem("auth_token")) return;            // skip logged-in
    const last = Number(localStorage.getItem(SEEN_KEY) || 0);
    if (last && Date.now() - last < SEVEN_DAYS) return;         // 7-day cap

    let timeReady = false, scrollReady = false, opened = false, timerId;
    const onScroll = () => {
      const sh = document.documentElement.scrollHeight - window.innerHeight;
      if (sh > 0 && window.scrollY / sh >= SCROLL_THRESHOLD) { scrollReady = true; maybeOpen(); }
    };
    function detach() { clearTimeout(timerId); window.removeEventListener("scroll", onScroll); }
    function maybeOpen() {
      if (opened || !timeReady || !scrollReady) return;
      opened = true;
      detach();
      openModal();
    }
    timerId = setTimeout(() => { timeReady = true; maybeOpen(); }, OPEN_AFTER_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    return detach;
  }, [openModal]);

  // Clear the auto-minimize timer on unmount.
  useEffect(() => () => clearTimeout(autoMinRef.current), []);

  const dismiss = useCallback(() => {
    clearTimeout(autoMinRef.current);
    setView("dismissed");
  }, []);

  const reopen = () => {
    track("welcome_chip_click");
    setView("modal");
    startAutoMinimize();  // give them another idle window before it re-minimizes
  };

  const startTrial = () => {
    track("welcome_modal_cta_click");
    clearTimeout(autoMinRef.current);
    setView("dismissed");
    onStartTrial?.();
  };

  // Scroll-lock + Escape + focus only while the full modal is open.
  useEffect(() => {
    if (view !== "modal") { document.body.style.overflow = ""; return; }
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const id = requestAnimationFrame(() => closeBtnRef.current?.focus?.());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      cancelAnimationFrame(id);
    };
  }, [view, dismiss]);

  const open = view === "modal";
  const receivables = useCountUp(18250000, open);
  const outstanding = useCountUp(4875000, open);
  const score = useCountUp(82, open, 1600);
  const attention = useCountUp(18, open, 1200);
  const settlements = useCountUp(12, open, 1200);
  const potential = useCountUp(720000, open);

  return (
    <>
      <div aria-live="polite" className="sr-only" data-testid="welcome-live">
        {view === "chip" ? "Money Stuck preview minimized. A button to reopen it is available." : ""}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="welcome-modal-overlay"
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:p-6"
            style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={dismiss}
          >
            <motion.div
              data-testid="welcome-modal"
              role="dialog" aria-modal="true" aria-labelledby="welcome-modal-title"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="relative my-6 w-full max-w-3xl"
              style={{
                borderRadius: 20,
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
                border: "1px solid rgba(255,255,255,0.6)",
                boxShadow: "0 30px 80px -20px rgba(11,61,145,0.35), 0 8px 24px rgba(15,23,42,0.12)",
              }}
            >
              <div aria-hidden style={{ borderRadius: "20px 20px 0 0" }} className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#0B3D91] via-[#3b82f6] to-[#16A34A]" />
              <button
                ref={closeBtnRef}
                onClick={dismiss} aria-label="Close" data-testid="welcome-modal-close"
                className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-slate-500 shadow-sm transition-colors hover:bg-white hover:text-slate-900"
              >
                <X size={18} strokeWidth={2.5} />
              </button>

              <div className="px-6 py-7 sm:px-10 sm:py-9" style={{ fontFamily: "'Inter', sans-serif" }}>
                <div className="text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B3D91]/10 text-3xl welcome-pulse">💰</div>
                  <h2 id="welcome-modal-title" className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    How Much Money Is <span className="text-[#0B3D91]">Stuck</span> With Your Customers?
                  </h2>
                  <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
                    Most businesses don't know. Their receivables are scattered across Excel sheets, WhatsApp, emails, phone calls and employees.
                    <span className="block mt-1 font-medium text-slate-800">RecoverOS transforms all of this into one intelligent Receivables Intelligence Platform.</span>
                  </p>
                </div>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 sm:p-5">
                  <div className="text-center text-xs font-bold uppercase tracking-widest text-[#0B3D91]">Money Stuck Analyzer™</div>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <PreviewCard emoji="💰" label="Total Receivables" value={inr(receivables)} />
                    <PreviewCard emoji="⚠️" label="Outstanding" value={inr(outstanding)} accent="#D97706" />
                    <PreviewCard emoji="📊" label="Recovery Health Score" value={`${Math.round(score)} / 100`} sub="Good" accent="#16A34A" />
                    <PreviewCard emoji="🎯" label="Accounts Requiring Attention" value={`${Math.round(attention)}`} />
                    <PreviewCard emoji="🤝" label="Settlement Opportunities" value={`${Math.round(settlements)}`} accent="#7C3AED" />
                    <PreviewCard emoji="💵" label="Potential Recovery This Month" value={inr(potential)} accent="#16A34A" />
                  </div>
                  <p className="mt-3 text-center text-xs italic text-slate-500">This is exactly what you'll see after uploading your Excel file.</p>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {FEATURES.map((f) => (
                    <div key={f} className="welcome-lift flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-800">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#16A34A]/15 text-[#16A34A]">✓</span>
                      {f}
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-[#0B3D91]/15 bg-[#0B3D91]/[0.04] p-5 text-center">
                  <div className="text-sm font-extrabold text-[#0B3D91]">🎁 INTRO PAYG — ₹0 FOR 3 MONTHS</div>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs font-semibold text-slate-700">
                    {["₹0 for 3 months", "Up to 100 accounts · 1 user", "Up to 250 Email Communications", "Includes up to 50 Free RC/month"].map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5"><span className="text-[#16A34A]">✔</span> {t}</span>
                    ))}
                  </div>
                  <button
                    onClick={startTrial} data-testid="welcome-modal-cta"
                    className="welcome-cta mt-5 w-full rounded-xl bg-[#0B3D91] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-[#0B3D91]/30 transition-all hover:bg-[#0a347d] hover:shadow-xl hover:-translate-y-0.5"
                  >
                    Start with Intro PAYG →
                  </button>
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-medium text-slate-500">
                  {TRUST.map(([e, t], i) => (
                    <span key={t} className="inline-flex items-center gap-1.5">
                      {i > 0 && <span className="text-slate-300">·</span>}
                      <span>{e}</span> {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {view === "chip" && (
          <motion.button
            data-testid="welcome-chip"
            onClick={reopen}
            aria-label="See Money Stuck preview"
            initial={{ opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="fixed bottom-6 right-6 z-[100] inline-flex items-center gap-2 rounded-full bg-[#0B3D91] px-5 py-3 text-sm font-bold text-white shadow-xl shadow-[#0B3D91]/30 transition-all hover:bg-[#0a347d] hover:-translate-y-0.5"
          >
            <span className="text-lg leading-none">💰</span> See Money Stuck™
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

function PreviewCard({ emoji, label, value, sub, accent = "#0B3D91" }) {
  return (
    <div className="welcome-lift rounded-xl border border-slate-200 bg-white p-3 text-center transition-transform">
      <div className="text-lg">{emoji}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 leading-tight">{label}</div>
      <div className="mt-1 text-base sm:text-lg font-extrabold tabular-nums" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] font-bold text-[#16A34A]">{sub}</div>}
    </div>
  );
}
